import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { PageHeader } from '@/components/PageHeader';
import { QueryState } from '@/components/QueryState';
import { RfqBadge, RfqMethodBadge } from '@/components/StatusBadge';
import { shortDateTime, untilLabel } from '@/lib/format';
import { useRfqs } from '@/lib/queries';
import type { VendorRfqRow } from '@/lib/types';

/**
 * 견적·입찰 — 우리가 초청받은 공고(DOC-98 §5-1).
 *
 * <p><b>현장이 경로에 없다.</b> 다른 화면은 전부 `/sites/:siteId/…` 인데 여기만 `/rfqs` 다 —
 * 공고가 현장을 들고 있고, 마감이 임박한 것을 현장별로 나눠 보게 하면 놓친다.
 *
 * <p>초청받지 않은 공고는 목록에 없는 정도가 아니라 <b>있는지조차</b> 오지 않는다(지명경쟁 · 결정 ⑦).
 * 그래서 「공개 공고 보기」 같은 자리를 만들지 않는다.
 */
export function RfqsPage() {
  const rfqs = useRfqs();
  const rows = useMemo(() => byDeadline(rfqs.data ?? []), [rfqs.data]);

  return (
    <>
      <PageHeader title="견적·입찰" back={false} />

      <QueryState
        pending={rfqs.isPending}
        error={rfqs.isError}
        empty={rows.length === 0}
        emptyText="받은 견적·입찰이 없습니다."
      >
        <ul className="flex flex-col gap-2">
          {rows.map((row) => (
            <li key={row.id}>
              <Link to={`/rfqs/${row.id}`} className="card block">
                <div className="mb-1 flex items-start justify-between gap-2">
                  <span className="min-w-0 flex-1 truncate text-ui-md font-medium">{row.title}</span>
                  <span className="flex shrink-0 items-center gap-1.5">
                    <RfqMethodBadge method={row.method} />
                    <RfqBadge status={row.status} closed={row.closed} />
                  </span>
                </div>

                <div className="flex items-center justify-between gap-2 text-ui-2xs" style={{ color: 'var(--text-muted)' }}>
                  <span className="min-w-0 truncate">
                    {row.siteName}
                    {row.trade !== null && ` · ${row.trade}`}
                  </span>
                  {/*
                    남은 시간은 **읽는 값**이지 판정이 아니다. 마감 여부는 서버가 준 `closed` 로만 가른다
                    (DOC-98 §4-1) — 기기 시계가 틀어져도 화면과 서버의 답이 갈리지 않게.
                  */}
                  <span className="shrink-0">
                    {row.closed ? `마감 ${shortDateTime(row.closesAt)}` : untilLabel(row.closesAt)}
                  </span>
                </div>

                {(row.mySubmitted || row.intent !== null) && (
                  <div className="mt-2 flex items-center gap-1.5">
                    {row.mySubmitted && <span className="badge badge-blue">제출함</span>}
                    {row.intent !== null && (
                      <span className={`badge ${row.intent === 'JOIN' ? 'badge-blue' : 'badge-gray'}`}>
                        {row.intent === 'JOIN' ? '참가' : '불참'}
                      </span>
                    )}
                  </div>
                )}
              </Link>
            </li>
          ))}
        </ul>
      </QueryState>
    </>
  );
}

/**
 * 마감이 임박한 것이 위로.
 *
 * <p>서버는 `closes_at DESC` 로 준다(본사 목록이 최근 공고를 먼저 보는 순서다). 협력사가 먼저 봐야
 * 하는 것은 <b>오늘 안에 내야 하는 것</b>이라 여기서 다시 세운다.
 *
 * <p>마감시각이 없는 공고는 재촉이 없으니 살아 있는 것들의 뒤로, 끝난 것은 맨 뒤로 보낸다.
 */
function byDeadline(rows: VendorRfqRow[]): VendorRfqRow[] {
  const rank = (row: VendorRfqRow) => (row.closed ? 2 : row.closesAt === null ? 1 : 0);
  const at = (value: string | null) => (value === null ? 0 : new Date(value).getTime());

  return rows.slice().sort((a, b) => {
    if (rank(a) !== rank(b)) return rank(a) - rank(b);
    // 살아 있는 것은 이른 마감이 먼저, 끝난 것은 최근에 끝난 것이 먼저다.
    if (rank(a) === 0) return at(a.closesAt) - at(b.closesAt);
    if (rank(a) === 2) return at(b.closesAt) - at(a.closesAt);
    return at(b.createdAt) - at(a.createdAt);
  });
}
